/**
 * Page-scoped help catalog for PublicNav `?`.
 * Glance lines: exactly 5, each ≤7 words. Details are user tips (not agent docs).
 */

import { BIO_MIN_LENGTH, getEarnCoins } from '../../services/users/earnCatalog';

/** @typedef {'events' | 'game' | 'places' | 'map' | 'friends' | 'profile' | 'sidequests' | 'shop' | 'vibecheck' | 'fallback'} HelpKey */

/**
 * @typedef {object} HelpSection
 * @property {string} heading
 * @property {string} body
 */

/**
 * @typedef {object} HelpEntry
 * @property {HelpKey} key
 * @property {string} title
 * @property {string[]} glance
 * @property {HelpSection[]} sections
 */

export const GLANCE_COUNT = 5;
export const GLANCE_MAX_WORDS = 7;

/**
 * @param {string} line
 * @returns {number}
 */
export function countWords(line) {
  return String(line || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

/**
 * @param {string} pathname
 * @param {string} [search]
 * @returns {HelpKey}
 */
export function resolveHelpKey(pathname, search = '') {
  const path = pathname || '/';
  const params = new URLSearchParams(search.startsWith('?') ? search : search ? `?${search}` : '');

  if (path === '/' || path === '/events' || path.startsWith('/events/')) return 'events';
  if (path === '/game' || path.startsWith('/game/collection')) return 'game';
  if (path.startsWith('/places') && params.get('view') === 'map') return 'map';
  if (path === '/places' || path.startsWith('/places/')) return 'places';
  if (path === '/friends' || path.startsWith('/friends/')) return 'friends';
  if (path === '/profile' || path.startsWith('/profile/')) return 'profile';
  if (path === '/sidequests' || path.startsWith('/sidequests/')) return 'sidequests';
  if (path === '/shop' || path.startsWith('/shop/')) return 'shop';
  if (
    path === '/vibecheck' ||
    path.startsWith('/vibecheck/') ||
    path === '/upload-vibecheck' ||
    path.startsWith('/upload-vibecheck/')
  ) {
    return 'vibecheck';
  }
  return 'fallback';
}

/** @type {Record<HelpKey, HelpEntry>} */
export const HELP_BY_KEY = {
  events: {
    key: 'events',
    title: 'Events',
    glance: [
      'Hand-picked Sarajevo happenings',
      'Tap poster for full details',
      'Like events to earn coins',
      'Secret events need sign-in',
      'Filter Best of and categories',
    ],
    sections: [
      {
        heading: 'Browse the calendar',
        body: 'Cards and the calendar show curated happenings. Tap a poster to open details with time, place, and how to go.',
      },
      {
        heading: 'Like & coins',
        body: 'Sign in, then like an event to keep it and earn +2 mCoins the first time you like each one.',
      },
      {
        heading: 'Secret events',
        body: 'Blurred posters and “Secret” locations stay locked until you sign in. After that, exclusive events reveal normally.',
      },
      {
        heading: 'Filters',
        body: 'Use Best of, categories, and pinned chips to narrow the list. Sticky Like and Share stay on the detail sheet.',
      },
      {
        heading: 'Friends want to go',
        body: 'When you are signed in and connected, you can see which friends liked the same event.',
      },
    ],
  },
  game: {
    key: 'game',
    title: 'Game',
    glance: [
      'Walk to unlock the city',
      'Find drops as you explore',
      'Progress needs a signed-in account',
      'Collection shows what you found',
      'Earn coins for pickups and posts',
    ],
    sections: [
      {
        heading: 'Fog of the city',
        body: 'The map starts grayed out. Walk (or move on the map with location) to clear fog and reveal streets around you.',
      },
      {
        heading: 'Drops',
        body: 'Photos, videos, and journals appear where people left them. Walk closer to unlock locked drops, then collect them.',
      },
      {
        heading: 'Sign in to keep progress',
        body: 'Guests can browse a preview, but fog progress and finds only stick after you create or sign into an account.',
      },
      {
        heading: 'Collection & My drops',
        body: 'Collection is what you picked up. Profile My drops is what you posted. Some drops stay blurred until you collect them on the map.',
      },
      {
        heading: 'mCoins',
        body: 'First pickup of someone else’s drop is +5. Creating a drop far enough from your earlier ones is +20. Too close to your last drop earns 0. Balance shows on Profile and Shop.',
      },
    ],
  },
  places: {
    key: 'places',
    title: 'Places',
    glance: [
      'Curated list of Sarajevo spots',
      'Search and filter by type',
      'Open a place for details',
      'Like places to earn coins',
      'Map tab shows the same pins',
    ],
    sections: [
      {
        heading: 'What Places is',
        body: 'A curated list of monuments, restaurants, bars, cafés, and things to do — ordered for discovery, not raw GPS distance.',
      },
      {
        heading: 'Open a place',
        body: 'Tap a row for hours, maps, and more. Like a place once you are signed in to keep it and earn +5 mCoins the first time.',
      },
      {
        heading: 'Places vs Map',
        body: 'This tab is the list. Switch to Map in the bottom nav for the same spots as pins on a map.',
      },
    ],
  },
  map: {
    key: 'map',
    title: 'Map',
    glance: [
      'Places shown as map pins',
      'Pan and zoom neighborhoods',
      'Tap a pin for details',
      'List view is the Places tab',
      'Not the fog Game map',
    ],
    sections: [
      {
        heading: 'Scout the city',
        body: 'See curated places as markers. Pan and zoom a neighborhood, then tap a pin to open the same place details as the list.',
      },
      {
        heading: 'Map vs Places vs Game',
        body: 'Map and Places share the same spots (list vs pins). Game is a separate fog explorer for walking and drops — not this map.',
      },
    ],
  },
  friends: {
    key: 'friends',
    title: 'Friends',
    glance: [
      'Search and add friends',
      'Accept requests from the bell',
      'See who wants to go',
      'Shared calendars after you connect',
      'Suggested people to follow',
    ],
    sections: [
      {
        heading: 'Requests',
        body: 'Incoming friend requests and memory invites show in the header bell. Accept, decline, or open from there. Accepting a friend (not sending the invite) earns +5 mCoins once per person.',
      },
      {
        heading: 'Chat',
        body: 'Message from a friend row or a public profile. Your first message to a friend earns +2 mCoins. Later messages and DMs to non-friends do not.',
      },
      {
        heading: 'Shared calendars',
        body: 'Once you are friends, you can view shared calendars. Non-friends see a blurred calendar until you connect.',
      },
      {
        heading: 'Going out together',
        body: 'See who wants to go to the same events, and share calendar links when you want someone to join.',
      },
    ],
  },
  profile: {
    key: 'profile',
    title: 'Profile',
    glance: [
      'Edit your name and photo',
      'Your calendar and liked plans',
      'My drops and coin balance',
      'Appearance unlocks in the Shop',
      'Phone alerts on this device',
    ],
    sections: [
      {
        heading: 'Your identity',
        body: 'Set username, photo, and a short bio. First username is +10 mCoins, first photo +5, and first bio of 12+ characters +5. Spending never lowers your level.',
      },
      {
        heading: 'Calendar & drops',
        body: 'Your calendar holds liked plans. My drops lists what you posted in Game. Public profiles may blur drops until collected on the map.',
      },
      {
        heading: 'Appearance',
        body: 'Dark Mode and other looks unlock in the Shop, then toggle under Appearance here — not in the header.',
      },
      {
        heading: 'Phone alerts',
        body: 'Turn on Phone alerts for friend requests and memory invites on your lock screen. iPhone: add MapToBe to the Home Screen first.',
      },
    ],
  },
  sidequests: {
    key: 'sidequests',
    title: 'Sidequests',
    glance: [
      'Curated missions around town',
      'Not the fog Game tab',
      'Open a mission for the route',
      'Progress saves on this device',
      'Loot previews rewards only',
    ],
    sections: [
      {
        heading: 'Missions, not fog',
        body: 'Sidequests are curated routes and checklists. The bottom-nav Game tab is the separate fog explorer with drops.',
      },
      {
        heading: 'Progress',
        body: 'Mission progress is stored on this device for now. Open a card for briefing, route nodes, and sponsor-style chrome when shown.',
      },
      {
        heading: 'Rewards',
        body: 'Loot UI previews what you might earn. Fulfillment may come later — treat rewards as a preview.',
      },
    ],
  },
  shop: {
    key: 'shop',
    title: 'Shop',
    glance: [
      'Spend mCoins you earned',
      'Unlock Dark Mode here',
      'Toggle theme on Profile',
      'Earn by saving and exploring',
      'One shared coin balance',
    ],
    sections: [
      {
        heading: 'Spend mCoins',
        body: 'Your Profile, Shop, and Game stats share one mCoin balance. Earn by saving events (+2) and places (+5), collecting drops (+5), posting spaced drops (+20), adding friends (+5), a first photo or bio (+5 each), and a first message to a friend (+2).',
      },
      {
        heading: 'Dark Mode',
        body: 'Buy Dark Mode in the Shop, then switch Dark/Light under Profile Appearance. The header does not host the theme toggle.',
      },
    ],
  },
  vibecheck: {
    key: 'vibecheck',
    title: 'Vibecheck',
    glance: [
      'Send us your event video',
      'We may post it on Instagram',
      'Keep this screen on',
      'Instagram handle is optional',
      'Drop a pin or type place',
    ],
    sections: [
      {
        heading: 'What this is',
        body: 'This hidden page is for sending a video from a night out. We may reupload it on Instagram. It is not a drop on the Game map.',
      },
      {
        heading: 'Upload',
        body: 'Drop videos to start uploading right away. Tap a clip to preview it. Upload Vibecheck then stamps Instagram, place, and pin. Keep this screen on.',
      },
    ],
  },
  fallback: {
    key: 'fallback',
    title: 'MapToBe',
    glance: [
      'Events for curated nights out',
      'Game unlocks the city by walking',
      'Places and Map for spots',
      'Friends for people and calendars',
      'Profile holds your identity',
    ],
    sections: [
      {
        heading: 'Bottom nav',
        body: 'Use Events, Game, Places, Map, Friends, and Profile in the bar at the bottom. Places is the list; Map is the same spots as pins.',
      },
      {
        heading: 'Sign in when asked',
        body: 'Saving, secret events, and keeping Game progress need an account. Sign in from the header — you stay on the same page after.',
      },
      {
        heading: 'This page',
        body: 'Open Help from any main tab for tips that match that screen. Tap ? again anytime you need a refresher.',
      },
    ],
  },
};

/**
 * @param {string} pathname
 * @param {string} [search]
 * @returns {HelpEntry}
 */
export function getHelpForLocation(pathname, search = '', coins = getEarnCoins()) {
  const key = resolveHelpKey(pathname, search);
  const entry = HELP_BY_KEY[key] || HELP_BY_KEY.fallback;
  return withLiveCoinCopy(entry, coins);
}

const COIN_SECTION_BODIES = {
  events: {
    'Like & coins': (coins) =>
      `Sign in, then like an event to keep it and earn +${coins.EVENT_SAVE} mCoins the first time you like each one.`,
  },
  game: {
    mCoins: (coins) =>
      `First pickup of someone else’s drop is +${coins.DROP_PICKUP}. Creating a drop far enough from your earlier ones is +${coins.DROP_CREATE}. Too close to your last drop earns 0. Balance shows on Profile and Shop.`,
  },
  places: {
    'Open a place': (coins) =>
      `Tap a row for hours, maps, and more. Like a place once you are signed in to keep it and earn +${coins.PLACE_SAVE} mCoins the first time.`,
  },
  friends: {
    Requests: (coins) =>
      `Incoming friend requests and memory invites show in the header bell. Accept, decline, or open from there. Accepting a friend (not sending the invite) earns +${coins.FRIEND} mCoins once per person.`,
    Chat: (coins) =>
      `Message from a friend row or a public profile. Your first message to a friend earns +${coins.FIRST_MESSAGE} mCoins. Later messages and DMs to non-friends do not.`,
  },
  profile: {
    'Your identity': (coins) =>
      `Set username, photo, and a short bio. First username is +${coins.USERNAME} mCoins, first photo +${coins.PHOTO}, and first bio of ${BIO_MIN_LENGTH}+ characters +${coins.BIO}. Spending never lowers your level.`,
  },
  shop: {
    'Spend mCoins': (coins) => {
      const photoAndBio =
        coins.PHOTO === coins.BIO
          ? `a first photo or bio (+${coins.PHOTO} each)`
          : `a first photo (+${coins.PHOTO}) or bio (+${coins.BIO})`;
      return `Your Profile, Shop, and Game stats share one mCoin balance. Earn by saving events (+${coins.EVENT_SAVE}) and places (+${coins.PLACE_SAVE}), collecting drops (+${coins.DROP_PICKUP}), posting spaced drops (+${coins.DROP_CREATE}), adding friends (+${coins.FRIEND}), ${photoAndBio}, and a first message to a friend (+${coins.FIRST_MESSAGE}).`;
    },
  },
};

function withLiveCoinCopy(entry, coins) {
  const overrides = COIN_SECTION_BODIES[entry.key];
  if (!overrides) return entry;
  return {
    ...entry,
    sections: entry.sections.map((section) => {
      const nextBody = overrides[section.heading];
      return nextBody ? { ...section, body: nextBody(coins) } : section;
    }),
  };
}
